/** A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. */

import { setContext, getContext } from "svelte";
import { getAbsoluteUrl } from "$utils/urls.js";
import { parseDate, formatQuarter, formatYear, parseYear } from "$utils/dates.js";
import { getName } from "$utils/geoNames.js";
import {
  MEDICATION_NAMES,
  BRANDS,
  METRICS,
  ALL_TIMES,
  YEARS,
  QUARTERS,
  BREAKDOWN_NAMES
} from "$utils/consts.js";
import states from "$data/states.json";

import Papa from "papaparse";

/** @typedef {import("$utils/types").DataObject} DataObject */

export class ToolData {
  meta = {
    metrics: METRICS,
    brands: BRANDS,
    mednames: MEDICATION_NAMES
  };

  /** @type {DataObject[] | undefined} */
  chartRawData = $state();
  /** @type {DataObject[] | undefined} */
  mapRawData = $state();

  /** @type {Map<string, DataObject[]>} */
  #chartCache = new Map();
  /** @type {Map<string, DataObject[]>} */
  #mapCache = new Map();

  /** @type {string | null} */
  activeGeo = $state("00");
  activeTime = $state("2025");
  activeMetric = $state("numberofprescriptions");
  activeFormat = $derived.by(() => this.getMetricFormat(this.activeMetric));
  activeMapCategory = $state("all");
  activeFrequency = $state("quarterly");
  activeMedType = $state("all");
  highlightCategory = $state(null);
  mapDataNeeded = $state(false);
  isBreakdownMode = $derived(this.activeMedType === "all");
  activeCategories = $derived(
    this.isBreakdownMode
      ? BREAKDOWN_NAMES
      : MEDICATION_NAMES.filter((d) => d.value !== "all").map((d) => d.value)
  );

  // Derive time/state lists from metadata instead of scanning data
  quarterList = QUARTERS;
  yearList = YEARS;
  stateList = states;
  timeList = ALL_TIMES;

  chartData = $derived.by(() => this.getChartData());
  mapData = $derived.by(() => this.getMapData());
  ticks = $derived.by(() => this.getTicks());

  /**
   * Parse a CSV response into data objects.
   * @param {string} text
   * @returns {DataObject[]}
   */
  parseCSV(text) {
    const processed = Papa.parse(text, {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: true,
      transform: this.transformValue
    });
    return processed.data;
  }

  /**
   * Fetch chart data for a specific geography.
   * @param {string} geoid
   */
  async fetchChartData(geoid) {
    if (this.#chartCache.has(geoid)) {
      this.chartRawData = this.#chartCache.get(geoid);
      return;
    }
    const res = await fetch(getAbsoluteUrl(`/data/chart/${geoid}.csv`));
    if (!res.ok) {
      throw new Error(`Failed to fetch chart data for geoid ${geoid}`);
    }
    const data = this.parseCSV(await res.text());
    this.#chartCache.set(geoid, data);
    this.chartRawData = data;
  }

  /**
   * Fetch map data for a given frequency (quarterly or annual).
   * @param {"quarterly" | "annual"} frequency
   */
  async fetchMapData(frequency) {
    const key = frequency === "annual" ? "annual" : "quarterly";
    if (this.#mapCache.has(key)) {
      this.mapRawData = this.#mapCache.get(key);
      return;
    }
    const filename = `map_${key}.csv`;
    const res = await fetch(getAbsoluteUrl(`/data/${filename}`));
    if (!res.ok) {
      throw new Error(`Failed to fetch map data: ${filename}`);
    }
    const data = this.parseCSV(await res.text());
    this.#mapCache.set(key, data);
    this.mapRawData = data;
  }

  /**
   * Determine which map frequency is needed based on activeTime.
   * Year-only values (e.g. "2025") use annual; quarter values use quarterly.
   * @returns {"quarterly" | "annual"}
   */
  getMapFrequency() {
    return this.activeTime.includes("Q") ? "quarterly" : "annual";
  }

  transformValue(value, field) {
    if (value === "NA") {
      return null;
    }
    if (field === "date") {
      return parseDate(value);
    }
    if (field === "geoid") {
      return value;
    }
    if (!isNaN(+value)) {
      return +value;
    }
    return value;
  }

  getChartData() {
    if (!this.chartRawData) return [];

    const mednames = MEDICATION_NAMES.filter((d) => d.value !== "all").map((d) => d.value);
    const categories = this.activeCategories;

    const filteredData = this.chartRawData.filter((d) => {
      const frequencyMatch =
        (this.activeFrequency === "annual" && !d.quarter) ||
        (this.activeFrequency === "quarterly" && d.quarter);
      if (this.isBreakdownMode) {
        return (
          frequencyMatch &&
          mednames.includes(d.medname) &&
          (d.medtype === "brand" || d.medtype === "generic")
        );
      }
      return frequencyMatch && mednames.includes(d.medname) && d.medtype === this.activeMedType;
    });

    let resultsByTime = new Map();
    for (const entry of filteredData) {
      const timeKey = entry.quarter ? formatQuarter(entry.date) : formatYear(entry.date);
      const seriesKey = this.isBreakdownMode ? `${entry.medname}_${entry.medtype}` : entry.medname;
      if (resultsByTime.has(timeKey)) {
        const existing = resultsByTime.get(timeKey);
        existing[seriesKey] = entry[this.activeMetric];
        resultsByTime.set(timeKey, existing);
      } else {
        /** @type {Record<string, unknown>} */
        const newEntry = { date: entry.date };
        newEntry[seriesKey] = entry[this.activeMetric];
        resultsByTime.set(timeKey, newEntry);
      }
      for (const cat of categories) {
        if (!resultsByTime.get(timeKey)[cat]) {
          resultsByTime.get(timeKey)[cat] = 0;
        }
      }
    }
    return Array.from(resultsByTime.values());
  }

  formatTime(d) {
    if (d.quarter) {
      return formatQuarter(d.date);
    }
    return formatYear(d.date);
  }

  getMapData() {
    if (!this.mapRawData) return [];
    return this.mapRawData
      .filter((d) => {
        const timeMatch = this.formatTime(d) == this.activeTime;
        const categoryMatch = this.activeMapCategory === d.medname;
        const brandMatch = this.activeMedType === d.medtype;

        return timeMatch && categoryMatch && brandMatch;
      })
      .map((d) => {
        return {
          map_id: d.geoid,
          value: d[this.activeMetric]
        };
      });
  }

  getMetricFormat(metric) {
    const metricInfo = this.getMetricMeta(metric);
    return metricInfo?.unit || "INT";
  }

  getMetricMeta(metric) {
    const metricInfo = this.meta.metrics.find((m) => m.metric === metric);
    return metricInfo;
  }

  getMetricLabel(metric) {
    const metricInfo = this.getMetricMeta(metric);
    return metricInfo?.full_name || metric;
  }

  /**
   * @param {string} cat
   * @returns {string}
   **/
  formatCategory(cat) {
    const lastUnderscore = cat.lastIndexOf("_");
    if (lastUnderscore !== -1 && (cat.endsWith("_brand") || cat.endsWith("_generic"))) {
      const medname = cat.slice(0, lastUnderscore);
      const type = cat.slice(lastUnderscore + 1);
      const medMeta = this.meta.mednames.find(({ value }) => value === medname);
      const medLabel = medMeta ? medMeta.label : medname;
      return `${medLabel} (${type.charAt(0).toUpperCase() + type.slice(1)})`;
    }
    let catMeta = this.meta.mednames.find(({ value }) => value === cat);
    if (!catMeta) {
      console.warn(`No metadata found for category: ${cat}`);
      return cat;
    }
    return catMeta.label;
  }

  getMapCategories() {
    return ["all", "naltrexone", "naloxone", "buprenorphine"];
  }

  getBrandLabel(brand) {
    let brandMeta = this.meta.brands.find(({ value }) => value === brand);
    return brandMeta.label;
  }

  getTicks() {
    return this.yearList.map(parseYear);
  }
}

const contextKey = Symbol("ToolData");

export function createToolData() {
  let toolData = new ToolData();
  setContext(contextKey, toolData);
  return toolData;
}

export function useToolData() {
  return getContext(contextKey);
}
